import { InstructionRow } from '@components/Common/InstructionRow';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiCard } from 'noddi-ui-web';
import { formatPhoneNumber } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import {
  useServiceCenter,
  useStationaryBookingCar,
  useStationaryBookingSelectedOrganization
} from '../../../stores/StationaryBookingStore';

type Props = {
  serviceDepartmentId?: number;
  licensePlate?: string;
  organizationName?: string;
  locationName?: string;
  confirmationPhoneNumber?: string | null;
  /** False when an organization booker has not opted in: only the car's drivers are texted. */
  isBookerTexted?: boolean;
};

export const StationaryBookingNextStepsFromStore = ({ isBookerTexted }: Pick<Props, 'isBookerTexted'>) => {
  const serviceCenter = useServiceCenter();
  const selectedOrganization = useStationaryBookingSelectedOrganization();
  const car = useStationaryBookingCar();

  return (
    <StationaryBookingNextSteps
      serviceDepartmentId={serviceCenter?.serviceDepartmentId}
      licensePlate={car?.licensePlate.number}
      organizationName={selectedOrganization?.organizationName}
      locationName={serviceCenter?.name}
      isBookerTexted={isBookerTexted}
    />
  );
};

export const StationaryBookingNextSteps = ({
  serviceDepartmentId,
  licensePlate,
  organizationName,
  locationName,
  confirmationPhoneNumber,
  isBookerTexted = true
}: Props) => {
  const { userData } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const manageBookingHost = `${currentBrand}.noddi.no`;
  const manageBookingLabel = t`${manageBookingHost} (opens in a new tab)`;
  const phoneNumber = formatPhoneNumber(
    confirmationPhoneNumber === undefined ? (userData?.user.phoneNumber ?? '') : (confirmationPhoneNumber ?? '')
  );
  const firstName = userData?.user.firstName;

  const { data: serviceDepartment, error: _serviceDepartmentError } = noddiAsync.useGet({
    type: URLKeys.getServiceDepartment,
    input: {
      serviceDepartmentId: serviceDepartmentId as number
    },
    queryConfig: {
      enabled: !!serviceDepartmentId
    }
  });

  const supportPhone = serviceDepartment?.supportPhone;
  const supportEmail = serviceDepartment?.supportEmail;
  const formattedSupportPhone = supportPhone ? formatPhoneNumber(supportPhone) : undefined;
  const emailSubject = encodeURIComponent(
    [t`Booking via Navio`, locationName, licensePlate, firstName, organizationName].filter(Boolean).join(' - ')
  );

  const smsText = !isBookerTexted
    ? t`The car's drivers will receive the SMS confirmation and a reminder the day before the appointment. You will not get booking SMS unless you turn them on above.`
    : phoneNumber
      ? t`You'll receive an SMS confirmation to ${phoneNumber} with your booking details within the next few minutes. You'll also receive a reminder SMS the day before your appointment. If you need to change the time of your booking, you can do so through the links in the SMS.`
      : t`You'll receive an SMS confirmation with your booking details within the next few minutes. You'll also receive a reminder SMS the day before your appointment. If you need to change the time of your booking, you can do so through the links in the SMS.`;

  return (
    <NoddiCard contentClassName='flex flex-col gap-3' title={t`What happens next?`}>
      <InstructionRow icon='MessageSquare' title={t`Confirmation`} description={smsText} />
      <InstructionRow
        icon='Calendar'
        title={t`Communication`}
        description={
          <span>
            <Trans>If you have any questions before your appointment, you can contact us</Trans>
            {formattedSupportPhone && (
              <>
                {' '}
                <Trans>
                  at{' '}
                  <a href={`tel:${supportPhone}`} className='font-medium underline'>
                    {formattedSupportPhone}
                  </a>
                </Trans>
              </>
            )}
            {supportEmail &&
              (formattedSupportPhone ? (
                <>
                  {' '}
                  <Trans>
                    or{' '}
                    <a href={`mailto:${supportEmail}?subject=${emailSubject}`} className='font-medium underline'>
                      {supportEmail}
                    </a>
                  </Trans>
                </>
              ) : (
                <>
                  {' '}
                  <Trans>
                    at{' '}
                    <a href={`mailto:${supportEmail}?subject=${emailSubject}`} className='font-medium underline'>
                      {supportEmail}
                    </a>
                  </Trans>
                </>
              ))}
            {'. '}
            {licensePlate && locationName ? (
              <Trans>
                Please reference your license plate <span className='font-medium'>{licensePlate}</span> and booking
                location <span className='font-medium'>{locationName}</span> when contacting us.
              </Trans>
            ) : licensePlate ? (
              <Trans>
                Please reference your license plate <span className='font-medium'>{licensePlate}</span> when contacting
                us.
              </Trans>
            ) : locationName ? (
              <Trans>
                Please reference your booking location <span className='font-medium'>{locationName}</span> when
                contacting us.
              </Trans>
            ) : null}
          </span>
        }
      />
      <InstructionRow
        icon='Globe'
        title={t`Manage your booking`}
        description={
          <span>
            <Trans>
              You can view and manage your existing bookings at{' '}
              <a
                href={`https://${manageBookingHost}`}
                target='_blank'
                rel='noopener noreferrer'
                className='font-medium underline'
                aria-label={manageBookingLabel}
              >
                {manageBookingHost}
              </a>
            </Trans>
          </span>
        }
      />
    </NoddiCard>
  );
};
